<template>
  <v-menu
    v-if="featureEnabled"
    v-model="menuOpen"
    bottom
    offset-y
    :nudge-bottom="4"
    :close-on-content-click="true"
    content-class="jdiad__menu-content"
    class="jdiad"
  >
    <template #activator="{ on, attrs }">
      <button
        type="button"
        class="jdiad__trigger"
        v-bind="attrs"
        v-on="on"
        aria-haspopup="menu"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        aria-label="Select an action"
        data-testid="jdiad-trigger"
      >
        <span class="jdiad__trigger-label">Select an action</span>
        <span
          class="jdiad__chevron"
          :class="{ 'jdiad__chevron--open': menuOpen }"
          aria-hidden="true"
        >
          <Icon name="chevron_down" />
        </span>
      </button>
    </template>

    <div class="jdiad__menu" role="menu" data-testid="jdiad-menu">
      <button
        v-for="item in menuItems"
        :key="item.id"
        type="button"
        class="jdiad__item"
        role="menuitem"
        :data-testid="`jdiad-item-${item.id}`"
        @click="onItemClick(item)"
      >
        <span class="jdiad__item-icon" aria-hidden="true">
          <Icon :name="item.icon" />
        </span>
        <span class="jdiad__item-label">{{ item.label }}</span>
      </button>
    </div>
  </v-menu>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import Icon from "@components/icons/Icon.vue";
import { LD_FLAG_IDEA_MDP_585 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11531 — JobDescriptionImportActionsDropDown (per-bullet actions dropdown).
 *
 * Rendered under each IQAIResponse chat bubble in the Import Job Description IQ
 * overlay. Resolves what happens to one extracted job-description bullet after
 * IQ's catalog-match step (Rule 6 of the Direct Responsibility Attachment +
 * AI-Assisted Import spec).
 *
 * Self-contained presentational component — the dropdown itself never writes.
 * Every action emits an event for the owning overlay to POST to
 * `.../job-description/execute/` (attach-role / attach-direct / create-new /
 * discard). Covering roles come from extract.
 *
 * Shipped behind the linked idea flag (idea-MDP-585). Renders by default; set
 * the flag to false to hide the whole dropdown until the overlay wires it.
 */
const props = defineProps({
  /** The extracted job-description bullet this dropdown resolves. */
  bulletText: {
    type: String,
    default: "",
  },
  /** Catalog match from position_import_match, if any. */
  matchedResponsibilityId: {
    type: [String, Number],
    default: null,
  },
  /** Active Roles already carrying the matched Responsibility. */
  coveringRoles: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "attach-role",
  "attach-direct",
  "create-new",
  "discard",
]);

const featureEnabled = ref(false);
const menuOpen = ref(false);
const proxy = getCurrentInstance()?.proxy;

const hasMatch = computed(() => !!props.matchedResponsibilityId);
const roleCount = computed(() => (props.coveringRoles || []).length);

const roleItems = computed(() =>
  (props.coveringRoles || []).map((role, index) => ({
    id: `attach-role-${index}`,
    kind: "attach-role",
    icon: "org_icon_role",
    label: roleLabel(index, roleCount.value),
    role,
  }))
);

const menuItems = computed(() => {
  const items = [
    ...roleItems.value,
    hasMatch.value
      ? {
          id: "attach-direct",
          kind: "attach-direct",
          icon: "org_icon_responsibility",
          label: "Attach matched responsibility directly to position",
        }
      : null,
    {
      id: "create-new",
      kind: "create-new",
      icon: "org_icon_responsibility",
      label: "Create and attach new responsibility",
    },
    {
      id: "discard",
      kind: "discard",
      icon: "trash_03",
      label: "Discard proposal and move to next",
    },
  ].filter(Boolean);

  return items;
});

/**
 * Single Role uses the unlabeled Figma copy; 2+ roles append
 * "containing matched responsibility" to "Attach role A/B/N".
 */
function roleLabel(index, total) {
  if (total <= 1) return "Attach role containing matched responsibility";
  return `Attach role ${roleLetter(index)} containing matched responsibility`;
}

function roleLetter(index) {
  if (index < 26) return String.fromCharCode(65 + index);
  // Beyond Z, fall back to a 1-based number so labels stay unambiguous.
  return String(index + 1);
}

function onItemClick(item) {
  switch (item.kind) {
    case "attach-role":
      emit("attach-role", { role_id: item.role.role_id });
      break;
    case "attach-direct":
      emit("attach-direct", { responsibility_id: props.matchedResponsibilityId });
      break;
    case "create-new":
      emit("create-new", createNewPayload());
      break;
    case "discard":
      emit("discard");
      break;
  }
  menuOpen.value = false;
}

/**
 * Prefill Create with the extracted bullet. Import JD bullets are often longer
 * than the catalog name limit, so the overlay turns the counter off.
 */
function createNewPayload() {
  const suggestedName = (props.bulletText || "").trim();
  return suggestedName ? { suggested_name: suggestedName } : {};
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_585, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dropdown shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.jdiad__trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 10px;
  background: #7f56d9;
  border: none;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
}

.jdiad__trigger:hover {
  background: #6941c6;
}

.jdiad__trigger:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.jdiad__chevron {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.jdiad__chevron :deep(svg) {
  width: 24px;
  height: 24px;
}

.jdiad__chevron--open {
  transform: rotate(180deg);
}

/* The chevron icon ships with a grey stroke; the trigger needs it white. */
.jdiad__chevron :deep(svg path[stroke="#667085"]) {
  stroke: #ffffff;
}

.jdiad__menu {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  background: #ffffff;
}

.jdiad__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0 6px;
  padding: 10px 10px 10px 8px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: #101828;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  max-width: calc(100% - 12px);
}

.jdiad__item:hover {
  background: #f9fafb;
}

.jdiad__item:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.jdiad__item-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #101828;
}

.jdiad__item-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.jdiad__item-label {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
}

.jdiad__item-icon :deep(svg [fill="#666666"]) {
  fill: currentColor;
}

.jdiad__item-icon :deep(svg [stroke="#666666"]) {
  stroke: currentColor;
}
</style>

<style>
/* Vuetify renders v-menu's content outside this component's scoped DOM, so the
   content-class wrapper must be styled globally (same pattern as the existing
   ResponsibilityAssignmentDropDown). */
.jdiad__menu-content {
  min-width: 366px;
  border: 1px solid #eaecf0 !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03) !important;
  overflow: hidden;
  z-index: 11010 !important;
}
</style>
